/**
 * Painel da Gerência — a tela do dono.
 *
 * Não está no menu de propósito: não é ferramenta de operação, é o lugar onde
 * o dono chega para saber como a serraria está, e sair. Menu é para trabalho
 * que se repete; isto é consulta.
 *
 * A ordem é: indicadores, gráficos, avisos. O dono chega para ver como a
 * serraria está, e só depois quer saber o que exige decisão dele.
 *
 * Os avisos não são texto escrito à mão: saem dos MESMOS números exibidos
 * acima, cada um carregando o valor que o disparou e para onde ir. Painel
 * que só mostra indicador deixa o dono descobrir o problema sozinho — e para
 * isso ele já tinha planilha.
 *
 * Nenhum número aqui é calculado na tela: tudo vem das views do servidor
 * (spec §2.6, "rendimento não é relatório que alguém escreve, é consulta").
 */
import { useEffect, useState } from "react";
import { api } from "../api.ts";
import {
  AneisDeCrescimento, GraoDeMadeira, Pinha, Pinheiro, ToraEmpilhada,
} from "../componentes/Ornamentos.tsx";

interface Painel {
  estoque_m3: string; rendimento_90d: string; bitolas_paradas: string;
  margem_mes: string; sem_custo_m3: string;
}
interface Posicao {
  a_receber: string; a_pagar: string;
  receber_vencido: string; pagar_vencido: string; titulos_vencidos: string;
}
interface Rendimento {
  especie: string; mes: string; tora_m3: string; serrada_m3: string;
  rendimento: string | null;
}
interface Margem {
  especie: string; mes: string; receita: string | null;
  custo: string | null; margem: string; sem_custo_m3: string;
}
interface Giro {
  codigo: string; especie: string;
  esp_mm: number | null; larg_mm: number | null; comp_m: string | null;
  em_estoque_m3: string; dias_parado: number | null;
}
interface Pendencia {
  id: string; tipo: string; motivo: string | null; registrado_em: string;
}
interface Noticia {
  titulo: string; link: string; fonte: string; publicadoEm: string | null;
}

const n3 = (v: string | null) =>
  v === null ? "—" : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const n2 = (v: string | null) =>
  v === null ? "—" : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const n0 = (v: number) => v.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
const mesCurto = (v: string) =>
  new Date(v).toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });

/* ------------------------------------------------------------- avisos */

type Grau = "critico" | "atencao" | "ok";
interface Aviso {
  grau: Grau; sinal: string; titulo: string; corpo: string; acao?: string;
}

/**
 * Os avisos são DERIVADOS dos números, nunca digitados.
 *
 * Cada um carrega o valor que o disparou e o que fazer — aviso sem número é
 * palpite, e aviso sem ação é só ansiedade.
 */
function montarAvisos(
  p: Painel | null, pos: Posicao | null,
  giro: Giro[], pend: Pendencia[],
): Aviso[] {
  const avisos: Aviso[] = [];
  if (!p || !pos) return avisos;

  if (Number(pos.titulos_vencidos) > 0) {
    avisos.push({
      grau: "critico", sinal: "!",
      titulo: `${pos.titulos_vencidos} título(s) vencido(s)`,
      corpo: `R$ ${n2(pos.receber_vencido)} já passaram do vencimento e não foram recebidos.`,
      acao: "Financeiro → filtrar por vencidas",
    });
  }

  if (Number(p.margem_mes) < 0) {
    avisos.push({
      grau: "critico", sinal: "!",
      titulo: "Margem negativa no mês",
      corpo: `O mês fecha em R$ ${n2(p.margem_mes)}. Custo de compra acima da receita de venda no período.`,
      acao: "Gerência → margem por espécie",
    });
  }

  if (Number(p.sem_custo_m3) > 0) {
    avisos.push({
      grau: "atencao", sinal: "?",
      titulo: "Margem incompleta",
      corpo:
        `${n3(p.sem_custo_m3)} m³ entraram ou saíram sem valor registrado e ficaram ` +
        `de fora da conta. Enquanto isso durar, a margem acima parece certa e não é.`,
      acao: "Informe o valor nas entradas de estoque",
    });
  }

  const rend = Number(p.rendimento_90d);
  if (rend > 0 && rend < 0.45) {
    avisos.push({
      grau: "atencao", sinal: "▼",
      titulo: `Rendimento em ${(rend * 100).toFixed(1)}%`,
      corpo:
        "Abaixo da faixa esperada para serra circular em pinus, que fica entre 45% e 55%. " +
        "Costuma ser afiação da serra ou tora fina demais.",
      acao: "Gerência → rendimento por espécie",
    });
  }

  const paradas = giro.filter((g) => (g.dias_parado ?? 0) > 60);
  if (paradas.length > 0) {
    const vol = paradas.reduce((s, g) => s + Number(g.em_estoque_m3), 0);
    avisos.push({
      grau: "atencao", sinal: "◷",
      titulo: `${paradas.length} bitola(s) parada(s) há mais de 60 dias`,
      corpo: `${n3(String(vol))} m³ ocupando pátio sem girar — é capital em cima de serragem.`,
      acao: "Gerência → giro por bitola",
    });
  }

  if (pend.length > 0) {
    avisos.push({
      grau: "atencao", sinal: "↯",
      titulo: `${pend.length} medição(ões) do pátio recusada(s)`,
      corpo:
        "O aparelho enviou e o servidor não aceitou. O trabalho não se perdeu, " +
        "mas também não entrou no estoque até alguém resolver.",
      acao: "Resolver a fila de pendências",
    });
  }

  if (avisos.length === 0) {
    avisos.push({
      grau: "ok", sinal: "✓",
      titulo: "Nada exigindo decisão",
      corpo: "Sem título vencido, sem estoque parado e sem medição recusada.",
    });
  }
  return avisos;
}

/* ------------------------------------------------------------- gráficos */

/**
 * Linha de rendimento no tempo.
 *
 * SVG na mão em vez de biblioteca: são vinte linhas, e uma dependência de
 * gráfico traz peso e tema próprio para brigar com os tokens.
 */
function LinhaRendimento({ dados }: { dados: { mes: string; valor: number }[] }) {
  if (dados.length < 2) {
    return (
      <div className="pg-vazio">
        <Pinheiro className="pg-vazio-svg" />
        <p>Poucos desdobros para desenhar tendência.</p>
      </div>
    );
  }
  const L = 44, R = 12, T = 14, B = 26, A = 100, C = 460;
  const maxY = 1;
  const px = (i: number) => L + (i * (C - L - R)) / (dados.length - 1);
  const py = (v: number) => T + (1 - v / maxY) * (A - T - B);
  const linha = dados.map((d, i) => `${px(i)},${py(d.valor)}`).join(" ");

  return (
    <svg viewBox={`0 0 ${C} ${A}`} className="pg-svg" role="img"
      aria-label="Rendimento por mês">
      {[0, 0.25, 0.5, 0.75, 1].map((g) => (
        <g key={g}>
          <line x1={L} x2={C - R} y1={py(g)} y2={py(g)} className="pg-grade" />
          <text x={L - 6} y={py(g) + 3} className="pg-eixo" textAnchor="end">
            {g * 100}%
          </text>
        </g>
      ))}
      {/* Faixa boa (45–55%) como contexto, não como decoração. */}
      <rect x={L} y={py(0.55)} width={C - L - R} height={py(0.45) - py(0.55)}
        className="pg-faixa" />
      <polyline points={linha} className="pg-linha" />
      {dados.map((d, i) => (
        <g key={d.mes}>
          <circle cx={px(i)} cy={py(d.valor)} r={4.5} className="pg-ponto" />
          <title>{`${mesCurto(d.mes)}: ${(d.valor * 100).toFixed(1)}%`}</title>
        </g>
      ))}
      {dados.map((d, i) => (
        <text key={d.mes} x={px(i)} y={A - 8} className="pg-eixo" textAnchor="middle">
          {mesCurto(d.mes)}
        </text>
      ))}
    </svg>
  );
}

/** Barras de margem por espécie. Uma cor só: é uma medida, não várias séries. */
function BarrasMargem({ dados }: { dados: { rotulo: string; valor: number }[] }) {
  if (dados.length === 0) {
    return (
      <div className="pg-vazio">
        <ToraEmpilhada className="pg-vazio-svg" />
        <p>Nenhuma compra ou venda no período.</p>
      </div>
    );
  }
  const max = Math.max(...dados.map((d) => Math.abs(d.valor)), 1);
  return (
    <div className="pg-barras">
      {dados.map((d) => {
        const neg = d.valor < 0;
        return (
          <div className="pg-barra" key={d.rotulo}>
            <span className="pg-barra-rot">{d.rotulo}</span>
            <div className="pg-barra-trilho">
              <div
                className={`pg-barra-fill${neg ? " neg" : ""}`}
                style={{ width: `${(Math.abs(d.valor) / max) * 100}%` }}
              />
            </div>
            {/* Rótulo direto: o contraste do âmbar não carrega o dado sozinho. */}
            <span className={`pg-barra-val${neg ? " neg" : ""}`}>
              R$ {n2(String(d.valor))}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------- tela */

type Tema = "escuro" | "claro";
const CHAVE_TEMA = "madeira.painel.tema";

/**
 * Tema guardado, com a preferência do sistema como primeira palavra.
 *
 * Quem já configurou o aparelho no claro não deveria precisar dizer de novo;
 * quem nunca disse nada recebe o escuro, que é a identidade do painel.
 */
function temaInicial(): Tema {
  try {
    const salvo = localStorage.getItem(CHAVE_TEMA);
    if (salvo === "claro" || salvo === "escuro") return salvo;
  } catch { /* modo privado */ }
  return window.matchMedia?.("(prefers-color-scheme: light)").matches
    ? "claro" : "escuro";
}

export function PainelGerencia({ aoFechar }: { aoFechar: () => void }) {
  const [tema, setTema] = useState<Tema>(temaInicial);

  function trocarTema() {
    const novo: Tema = tema === "escuro" ? "claro" : "escuro";
    setTema(novo);
    try { localStorage.setItem(CHAVE_TEMA, novo); } catch { /* modo privado */ }
  }

  const [p, setP] = useState<Painel | null>(null);
  const [pos, setPos] = useState<Posicao | null>(null);
  const [rend, setRend] = useState<Rendimento[]>([]);
  const [marg, setMarg] = useState<Margem[]>([]);
  const [giro, setGiro] = useState<Giro[]>([]);
  const [pend, setPend] = useState<Pendencia[]>([]);
  const [carregando, setCarregando] = useState(true);
  /**
   * Notícias entram DEPOIS e por conta própria.
   *
   * Dependem de site de terceiro; se um portal do setor estiver fora do ar,
   * o dono não pode ficar sem ver a margem por causa disso.
   */
  const [noticias, setNoticias] = useState<Noticia[] | null>(null);

  useEffect(() => {
    Promise.allSettled([
      api.get<Painel>("/gerencia/painel"),
      api.get<Posicao>("/financeiro/posicao"),
      api.get<{ linhas: Rendimento[] }>("/gerencia/rendimento"),
      api.get<{ linhas: Margem[] }>("/gerencia/margem"),
      api.get<{ linhas: Giro[] }>("/gerencia/giro"),
      api.get<{ pendencias: Pendencia[] }>("/sync/pendencias"),
    ]).then((r) => {
      if (r[0].status === "fulfilled") setP(r[0].value);
      if (r[1].status === "fulfilled") setPos(r[1].value);
      if (r[2].status === "fulfilled") setRend(r[2].value.linhas);
      if (r[3].status === "fulfilled") setMarg(r[3].value.linhas);
      if (r[4].status === "fulfilled") setGiro(r[4].value.linhas);
      if (r[5].status === "fulfilled") setPend(r[5].value.pendencias);
      setCarregando(false);
    });

    void api.get<{ itens: Noticia[] }>("/noticias")
      .then((r) => setNoticias(r.itens))
      .catch(() => setNoticias([]));
  }, []);

  /* Esc fecha: é tela de consulta, entra e sai. */
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => { if (e.key === "Escape") aoFechar(); };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [aoFechar]);

  const avisos = montarAvisos(p, pos, giro, pend);

  /* Rendimento agregado por mês, ordenado no tempo. */
  const serieRend = [...new Map(
    rend
      .filter((l) => l.rendimento !== null)
      .map((l) => [l.mes, { mes: l.mes, valor: Number(l.rendimento) }]),
  ).values()].sort((a, b) => a.mes.localeCompare(b.mes));

  const serieMargem = [...marg.reduce((m, l) => {
    m.set(l.especie, (m.get(l.especie) ?? 0) + Number(l.margem));
    return m;
  }, new Map<string, number>())].map(([rotulo, valor]) => ({ rotulo, valor }));

  const liquido = pos ? Number(pos.a_receber) - Number(pos.a_pagar) : 0;

  return (
    <div className="pg" data-tema={tema}>
      <header className="pg-topo">
        {/* Marca-d'água: anel de crescimento sangrando no canto. */}
        <AneisDeCrescimento className="pg-marca-agua" />
        <span className="pg-selo"><Pinha className="pg-selo-svg" /></span>
        <div className="pg-titulo">
          <h1>Painel da Gerência</h1>
          <p>
            Serraria Pioneira · {new Date().toLocaleDateString("pt-BR", {
              day: "2-digit", month: "long", year: "numeric",
            })}
          </p>
        </div>
        <button
          className="pg-tema" onClick={trocarTema}
          aria-pressed={tema === "claro"}
          title={tema === "escuro" ? "Mudar para tema claro" : "Mudar para tema escuro"}
        >
          {/* Sol e lua desenhados aqui mesmo: são doze linhas e evitam
              carregar um pacote de ícone inteiro por dois glifos. */}
          {tema === "escuro" ? (
            <svg viewBox="0 0 24 24" aria-hidden="true" fill="none"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <circle cx="12" cy="12" r="4.4" />
              {[0, 45, 90, 135, 180, 225, 270, 315].map((g) => (
                <line key={g} x1="12" y1="2.6" x2="12" y2="5"
                  transform={`rotate(${g} 12 12)`} />
              ))}
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" aria-hidden="true" fill="none"
              stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
              <path d="M20 14.4A8.6 8.6 0 0 1 9.6 4 8.6 8.6 0 1 0 20 14.4Z" />
            </svg>
          )}
          <span>{tema === "escuro" ? "Claro" : "Escuro"}</span>
        </button>
        <button className="pg-sair" onClick={aoFechar}>
          Voltar ao sistema <kbd>Esc</kbd>
        </button>
        <GraoDeMadeira className="pg-grao" />
      </header>

      {carregando ? (
        <div className="pg-corpo"><p className="pg-vazio">Carregando…</p></div>
      ) : (
        <div className="pg-corpo">
          <section className="pg-kpis">
            <Kpi rotulo="Estoque no pátio" valor={`${n3(p?.estoque_m3 ?? null)}`} unidade="m³" />
            <Kpi
              rotulo="Rendimento 90 dias"
              valor={p ? (Number(p.rendimento_90d) * 100).toFixed(1) : "—"}
              unidade="%"
              nota={p ? (Number(p.rendimento_90d) >= 0.45 ? "dentro da faixa" : "abaixo da faixa") : ""}
              grau={p ? (Number(p.rendimento_90d) >= 0.45 ? "ok" : "atencao") : undefined}
            />
            <Kpi
              rotulo="A receber"
              valor={n2(pos?.a_receber ?? null)} unidade="R$"
              nota={pos && Number(pos.titulos_vencidos) > 0
                ? `${pos.titulos_vencidos} vencido(s)` : "nenhum vencido"}
              grau={pos && Number(pos.titulos_vencidos) > 0 ? "critico" : "ok"}
            />
            <Kpi
              rotulo="Posição líquida"
              valor={n2(String(liquido))} unidade="R$"
              nota="a receber − a pagar"
              grau={liquido < 0 ? "critico" : undefined}
            />
          </section>

          <section className="pg-graficos">
            <article className="pg-cartao">
              <header>
                <h2>Rendimento por mês</h2>
                <span>serrada ÷ tora · faixa esperada 45–55% em destaque</span>
              </header>
              <LinhaRendimento dados={serieRend} />
            </article>

            <article className="pg-cartao">
              <header>
                <h2>Margem por espécie</h2>
                <span>receita menos custo, acumulado</span>
              </header>
              <BarrasMargem dados={serieMargem} />
            </article>
          </section>

          {/*
            Avisos depois dos indicadores: o dono chega para ver como está, e
            só então quer saber o que exige decisão. Continuam DERIVADOS dos
            mesmos números acima — cada um carrega o valor que o disparou.
          */}
          <section className="pg-avisos">
            {avisos.map((a) => (
              <article className={`pg-aviso ${a.grau}`} key={a.titulo}>
                <span className="pg-aviso-sinal" aria-hidden="true">{a.sinal}</span>
                <div>
                  <h3>{a.titulo}</h3>
                  <p>{a.corpo}</p>
                  {a.acao && <span className="pg-aviso-acao">{a.acao}</span>}
                </div>
              </article>
            ))}
          </section>

          <section className="pg-cartao">
            <header>
              <h2>O setor hoje</h2>
              <span>IBÁ e Google Notícias · atualiza a cada 30 minutos</span>
            </header>
            {noticias === null ? (
              <p className="pg-noticias-carregando">Buscando…</p>
            ) : noticias.length === 0 ? (
              <div className="pg-vazio">
                <Pinheiro className="pg-vazio-svg" />
                <p>Nenhuma fonte respondeu agora. Tente mais tarde.</p>
              </div>
            ) : (
              <ul className="pg-noticias">
                {noticias.map((n) => (
                  <li key={n.link}>
                    {/* Conteúdo de terceiro: abre fora, e o rel fecha a porta
                        do window.opener para a página de destino. */}
                    <a href={n.link} target="_blank" rel="noopener noreferrer">
                      <span className="pg-noticia-titulo">{n.titulo}</span>
                      <span className="pg-noticia-pe">
                        {n.fonte}
                        {n.publicadoEm && ` · ${new Date(n.publicadoEm)
                          .toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}`}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {giro.filter((g) => (g.dias_parado ?? 0) > 60).length > 0 && (
            <section className="pg-cartao">
              <header>
                <h2>Parado no pátio</h2>
                <span>bitolas sem venda há mais de 60 dias</span>
              </header>
              <table className="pg-tabela">
                <thead>
                  <tr>
                    <th>Produto</th><th>Bitola</th>
                    <th className="r">Estoque m³</th><th className="r">Parado</th>
                  </tr>
                </thead>
                <tbody>
                  {giro.filter((g) => (g.dias_parado ?? 0) > 60).map((g, i) => (
                    <tr key={i}>
                      <td>{g.codigo}</td>
                      <td>
                        {g.esp_mm === null ? "tora" : `${g.esp_mm} × ${g.larg_mm} × ${g.comp_m}`}
                      </td>
                      <td className="r">{n3(g.em_estoque_m3)}</td>
                      <td className="r">{n0(g.dias_parado ?? 0)} d</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function Kpi({
  rotulo, valor, unidade, nota, grau,
}: {
  rotulo: string; valor: string; unidade: string;
  // `| undefined` explícito: com exactOptionalPropertyTypes a chave opcional
  // não aceita undefined vindo de uma expressão condicional.
  nota?: string | undefined; grau?: Grau | undefined;
}) {
  return (
    <article className="pg-kpi">
      <span className="pg-kpi-rot">{rotulo}</span>
      <span className="pg-kpi-val">
        {unidade === "R$" && <i>R$ </i>}{valor}
        {unidade !== "R$" && <i> {unidade}</i>}
      </span>
      {nota && <span className={`pg-kpi-nota${grau ? ` ${grau}` : ""}`}>{nota}</span>}
    </article>
  );
}
